import { useState } from "react";
import { Link } from "wouter";
import {
  ArrowLeft, Copy, Check, ChevronDown, ChevronRight,
  UserPlus, Mail, ShieldCheck, BookOpen, Link2, Star,
  CheckCircle2, Clock, AlertCircle, Info, Users, Zap,
  FileText, Award, Eye
} from "lucide-react";
import { toast } from "sonner";

// ─── Reusable Components ──────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success("Copied to clipboard!");
      }}
      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-[#84ab4a]/10 text-[#84ab4a] hover:bg-[#84ab4a]/20 transition-colors"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

function EmailTemplate({
  subject,
  body,
  tag,
  tagColor,
}: {
  subject: string;
  body: string;
  tag: string;
  tagColor: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mt-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide"
            style={{ background: `${tagColor}18`, color: tagColor }}
          >
            {tag}
          </span>
          <span className="text-sm font-semibold text-gray-800">
            Subject: <span className="font-normal text-gray-600">{subject}</span>
          </span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Email Body</span>
            <CopyButton text={`Subject: ${subject}\n\n${body}`} />
          </div>
          <pre className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap font-sans bg-gray-50 rounded-xl p-4 border border-gray-100">
            {body}
          </pre>
          <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
            <Info className="w-3 h-3" />
            Merge tags like <code className="bg-gray-100 px-1 rounded text-gray-600">{"{{first_name}}"}</code> are filled automatically by your email platform.
          </p>
        </div>
      )}
    </div>
  );
}

function StageCard({
  number,
  title,
  icon: Icon,
  color,
  description,
  who,
  trigger,
  children,
}: {
  number: number;
  title: string;
  icon: any;
  color: string;
  description: string;
  who: string;
  trigger?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="relative flex gap-5">
      {/* Connector line */}
      <div className="flex flex-col items-center">
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm"
          style={{ background: `${color}15`, border: `1.5px solid ${color}40` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        {number < 6 && <div className="w-px flex-1 mt-2" style={{ background: `${color}25`, minHeight: "2rem" }} />}
      </div>

      {/* Content */}
      <div className="flex-1 pb-8">
        <div className="flex items-start gap-3 mb-1">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Step {number}</span>
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide"
                style={{ background: `${color}15`, color }}
              >
                {who}
              </span>
            </div>
            <h3 className="text-base font-bold text-gray-900 mt-0.5">{title}</h3>
          </div>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed mb-2">{description}</p>
        {trigger && (
          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 w-fit">
            <Zap className="w-3 h-3 flex-shrink-0" />
            <span><strong>Triggers:</strong> {trigger}</span>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function InfoBox({ type = "info", children }: { type?: "info" | "warning" | "tip"; children: React.ReactNode }) {
  const styles = {
    info: { bg: "bg-blue-50 border-blue-200", text: "text-blue-800", icon: Info, iconColor: "text-blue-500" },
    warning: { bg: "bg-amber-50 border-amber-200", text: "text-amber-800", icon: AlertCircle, iconColor: "text-amber-500" },
    tip: { bg: "bg-green-50 border-green-200", text: "text-green-800", icon: CheckCircle2, iconColor: "text-green-500" },
  };
  const s = styles[type];
  const IconComp = s.icon;
  return (
    <div className={`flex gap-2.5 border rounded-xl px-4 py-3 text-sm my-3 ${s.bg} ${s.text}`}>
      <IconComp className={`w-4 h-4 flex-shrink-0 mt-0.5 ${s.iconColor}`} />
      <div>{children}</div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AgentRegistrationGuide() {
  const [activeSection, setActiveSection] = useState<string>("overview");

  const sections = [
    { id: "overview", label: "Overview", icon: Eye },
    { id: "flow", label: "Registration Flow", icon: UserPlus },
    { id: "emails", label: "Email Templates", icon: Mail },
    { id: "hierarchy", label: "Agent Hierarchy", icon: Users },
    { id: "referral", label: "Referral Links", icon: Link2 },
    { id: "certifications", label: "Certifications", icon: Award },
  ];

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link href="/" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
            <span className="text-gray-300">|</span>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#84ab4a]/15 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-[#84ab4a]" />
              </div>
              <span className="font-bold text-gray-900 text-sm">Agent Registration Guide</span>
            </div>
          </div>
          <span className="text-xs text-gray-400 hidden sm:block">BizConnect360 — Internal Reference</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex gap-8">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-52 flex-shrink-0">
          <div className="sticky top-20">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">Contents</p>
            <nav className="flex flex-col gap-0.5">
              {sections.map(({ id, label, icon: Icon }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={() => setActiveSection(id)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                    activeSection === id
                      ? "bg-[#84ab4a]/10 text-[#84ab4a]"
                      : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  {label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 space-y-8">

          {/* Hero */}
          <div id="overview" className="bg-gradient-to-br from-[#0D1E35] to-[#1a3a5c] rounded-2xl p-8 text-white">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#84ab4a]/20 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-[#84ab4a]" />
              </div>
              <div>
                <p className="text-[10px] text-[#84ab4a] font-bold uppercase tracking-widest">BizConnect360</p>
                <h1 className="text-xl font-bold">Agent Registration Flow</h1>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-2xl">
              This guide walks through the complete process of onboarding a new sales agent — from the moment an admin adds them in the dashboard, through identity verification, approval, and their first referral link going live. Use this as a reference when setting up your email automations or training your admin team.
            </p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Steps in the flow", value: "6", color: "#84ab4a" },
                { label: "Email touchpoints", value: "4", color: "#1A6FD4" },
                { label: "Agent levels", value: "3", color: "#E8620A" },
                { label: "Verticals available", value: "6", color: "#9B59B6" },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-white/8 rounded-xl px-4 py-3">
                  <div className="text-2xl font-bold" style={{ color }}>{value}</div>
                  <div className="text-[11px] text-white/50 mt-0.5">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Registration Flow */}
          <section id="flow" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-xl bg-[#84ab4a]/10 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-[#84ab4a]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Registration Flow</h2>
                <p className="text-xs text-gray-500">Step-by-step from onboarding to active agent</p>
              </div>
            </div>

            <StageCard
              number={1}
              title="Admin Adds the New Agent"
              icon={UserPlus}
              color="#84ab4a"
              who="Admin"
              description="The admin opens the BC360 Admin Dashboard, goes to the Reps tab, and clicks Add New Rep. They fill in the agent's basic information and assign their role in the organization."
              trigger="Agent record created in BC360 + contact created in Wibiz Dashboard"
            >
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Fields the admin fills in:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { field: "First & Last Name", note: "Agent's legal name" },
                    { field: "Email Address", note: "Used for all notifications" },
                    { field: "Phone Number", note: "Optional" },
                    { field: "Primary Vertical", note: "e.g. Fundable360, Scale360" },
                    { field: "Agent Level", note: "Agency / Senior Associate / Associate" },
                    { field: "Upline Rep Code", note: "Direct upline's BC360 code (if applicable)" },
                    { field: "Agency Rep Code", note: "Top-level Agency code (if applicable)" },
                    { field: "Authorized Verticals", note: "Which products the agent can sell" },
                  ].map(({ field, note }) => (
                    <div key={field} className="flex items-start gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#84ab4a] flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-semibold text-gray-800">{field}</span>
                        <span className="text-xs text-gray-500"> — {note}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <InfoBox type="tip">
                The system automatically assigns a unique <strong>Rep Code</strong> (e.g. BC-360, BC-361) and generates the agent's referral links. The admin does not need to create these manually.
              </InfoBox>
            </StageCard>

            <StageCard
              number={2}
              title="Welcome Email Sent to Agent"
              icon={Mail}
              color="#1A6FD4"
              who="Automated"
              description="As soon as the agent is added, an automated welcome email is sent to their registered email address. This email introduces them to BizConnect360, provides their Rep Code, and gives them a link to their personal dashboard."
              trigger="Email sent immediately on agent creation"
            >
              <InfoBox type="info">
                The welcome email is the agent's first touchpoint. It should feel warm and professional — they've just joined the team. See the <a href="#emails" className="font-semibold underline" onClick={() => setActiveSection("emails")}>Email Templates</a> section below for the full content.
              </InfoBox>
            </StageCard>

            <StageCard
              number={3}
              title="Agent Submits Identity Verification"
              icon={ShieldCheck}
              color="#E8620A"
              who="Agent"
              description="The agent receives a follow-up email asking them to submit a government-issued ID for identity verification. This is a required step before their account is fully activated and their referral links go live."
              trigger="ID Verification Request email sent 1–2 hours after welcome"
            >
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-xs font-bold text-orange-700 mb-2">What the agent needs to provide:</p>
                <ul className="space-y-1">
                  {[
                    "Government-issued photo ID (driver's license or passport)",
                    "Clear, readable photo or scan — both sides if applicable",
                    "Submitted via the secure link in the verification email",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-orange-800">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </StageCard>

            <StageCard
              number={4}
              title="Admin Reviews the ID"
              icon={Eye}
              color="#9B59B6"
              who="Admin"
              description="The admin reviews the submitted ID in the BC360 Admin Dashboard under the Reps tab. They open the agent's detail drawer and update the pipeline stage in the Wibiz Dashboard accordingly."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                <div className="border border-green-200 bg-green-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-bold text-green-800">If Approved</span>
                  </div>
                  <p className="text-xs text-green-700">Move the agent's Wibiz Dashboard stage to <strong>"Approved"</strong>. The approval email is sent automatically.</p>
                </div>
                <div className="border border-red-200 bg-red-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-xs font-bold text-red-800">If Rejected</span>
                  </div>
                  <p className="text-xs text-red-700">Move the stage to <strong>"Rejected"</strong>. A rejection email is sent with instructions to resubmit.</p>
                </div>
              </div>
            </StageCard>

            <StageCard
              number={5}
              title="Agent Receives Approval & Logs In"
              icon={Star}
              color="#F59E0B"
              who="Agent"
              description="Once approved, the agent receives their official approval email with their Rep Code, a direct link to their personal dashboard, and instructions on how to get started. They can now log in and access their pipeline, referral links, and marketing materials."
              trigger="Approval email sent when Wibiz Dashboard stage = Approved"
            >
              <InfoBox type="tip">
                The agent's dashboard is accessible at <strong>{window.location.host}/rep/[RepCode]</strong>. No password is needed — they access it directly via their unique link.
              </InfoBox>
            </StageCard>

            <StageCard
              number={6}
              title="Agent Completes Certifications & Unlocks Referral Links"
              icon={Award}
              color="#84ab4a"
              who="Agent"
              description="Before referral links go live, the agent must complete a short certification quiz for each vertical they want to sell. This ensures every agent understands the product they're promoting. Once they pass (70% or above), their referral links for that vertical unlock immediately."
              trigger="Referral links unlock per vertical upon passing the certification quiz"
            >
              <div className="bg-[#84ab4a]/5 border border-[#84ab4a]/20 rounded-xl p-4">
                <p className="text-xs font-bold text-[#84ab4a] mb-2">Available certifications:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {["Scale360", "Fundable360", "Legacy360", "Share360 Business", "Share360 Nonprofit", "Media360"].map((v) => (
                    <div key={v} className="flex items-center gap-1.5 text-xs text-gray-700">
                      <BookOpen className="w-3 h-3 text-[#84ab4a]" />
                      {v}
                    </div>
                  ))}
                </div>
              </div>
            </StageCard>
          </section>

          {/* Email Templates */}
          <section id="emails" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Email Templates</h2>
                <p className="text-xs text-gray-500">Suggested content for each automated touchpoint</p>
              </div>
            </div>
            <InfoBox type="info">
              These are suggested templates — feel free to edit the tone, branding, or details to match your voice. Merge tags like <code className="bg-blue-100 px-1 rounded text-xs">{"{{first_name}}"}</code> and <code className="bg-blue-100 px-1 rounded text-xs">{"{{rep_code}}"}</code> are filled automatically by your email platform (e.g. Wibiz Dashboard).
            </InfoBox>

            <EmailTemplate
              tag="Step 2 — Sent Immediately"
              tagColor="#84ab4a"
              subject="Welcome to BizConnect360, {{first_name}}! 🎉"
              body={`Hi {{first_name}},

Welcome to the BizConnect360 team — we're excited to have you on board!

Your account has been created and you've been assigned your unique Rep Code:

    Rep Code: {{rep_code}}

Here's what happens next:

1. You'll receive a separate email shortly asking you to verify your identity. This is a quick, one-time step required to activate your account.

2. Once your ID is verified and approved, you'll receive your official welcome with your dashboard link and referral links.

3. After approval, you'll complete short certification quizzes for each product vertical you want to sell — this unlocks your referral links.

If you have any questions in the meantime, don't hesitate to reach out to your upline or the BC360 admin team.

Talk soon,
The BizConnect360 Team
info@BizConnect360.com | 979-323-6599`}
            />

            <EmailTemplate
              tag="Step 3 — Sent 1–2 Hours After Welcome"
              tagColor="#E8620A"
              subject="Action Required: Please Verify Your Identity — {{first_name}}"
              body={`Hi {{first_name}},

Before your BizConnect360 account is fully activated, we need to verify your identity. This is a standard step we require for all new agents.

What you need to do:

Please submit a clear photo or scan of your government-issued ID (driver's license or passport) using the link below:

    Submit Your ID → [Insert secure upload link here]

A few tips:
• Make sure the photo is clear and all text is readable
• Include both sides if it's a driver's license
• File formats accepted: JPG, PNG, or PDF

Once we receive and review your ID, you'll hear back from us within 1 business day.

Questions? Reply to this email or contact your upline directly.

Thank you,
The BizConnect360 Team
info@BizConnect360.com | 979-323-6599`}
            />

            <EmailTemplate
              tag="Step 5 — Sent on Approval"
              tagColor="#22c55e"
              subject="You're Approved! Your BizConnect360 Dashboard is Ready 🚀"
              body={`Hi {{first_name}},

Great news — your identity has been verified and your BizConnect360 account is now fully active!

Here's everything you need to get started:

YOUR REP CODE: {{rep_code}}
YOUR DASHBOARD: {{dashboard_url}}/rep/{{rep_code}}

─────────────────────────────────
NEXT STEPS
─────────────────────────────────

1. LOG IN TO YOUR DASHBOARD
   Visit your personal dashboard at the link above. You'll find your pipeline, leads, commissions, and marketing materials all in one place.

2. COMPLETE YOUR CERTIFICATIONS
   Before your referral links go live, you'll need to pass a short quiz for each product vertical you want to sell. Head to the Certifications tab in your dashboard to get started.

3. SHARE YOUR REFERRAL LINKS
   Once certified, your unique referral links will unlock. Share them with prospects and every lead will be automatically tagged to your Rep Code.

We're here to support you every step of the way. Welcome to the team!

The BizConnect360 Team
info@BizConnect360.com | 979-323-6599`}
            />

            <EmailTemplate
              tag="Step 4 — Sent on Rejection"
              tagColor="#ef4444"
              subject="ID Verification — Action Needed, {{first_name}}"
              body={`Hi {{first_name}},

Thank you for submitting your ID. Unfortunately, we weren't able to verify it with the submission we received.

This can happen for a few reasons:
• The image was blurry or too dark to read
• Only one side was submitted (we need both sides for a driver's license)
• The ID appears expired

Please resubmit using the link below with a clearer photo:

    Resubmit Your ID → [Insert secure upload link here]

If you believe this is an error or need help, please reply to this email or contact your upline directly.

We want to get you approved as quickly as possible!

The BizConnect360 Team
info@BizConnect360.com | 979-323-6599`}
            />
          </section>

          {/* Agent Hierarchy */}
          <section id="hierarchy" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Agent Hierarchy</h2>
                <p className="text-xs text-gray-500">How the three agent levels relate to each other</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {[
                {
                  level: "Agency",
                  color: "#F59E0B",
                  badge: "bg-yellow-100 text-yellow-800",
                  desc: "Top of the organization. Can have Senior Associates and Associates in their downline. Earns override commissions on all downline production.",
                  can: ["Recruit Senior Associates & Associates", "Earn override commissions on downline", "Access all verticals (if authorized)"],
                },
                {
                  level: "Senior Associate",
                  color: "#1A6FD4",
                  badge: "bg-blue-100 text-blue-800",
                  desc: "Mid-level agent. Must be placed under an Agency. Can recruit Associates into their Agency's organization.",
                  can: ["Recruit Associates (join under their Agency)", "Earn standard commissions on own deals", "Access authorized verticals"],
                },
                {
                  level: "Associate",
                  color: "#9B59B6",
                  badge: "bg-purple-100 text-purple-800",
                  desc: "Entry-level agent. Can be placed under an Agency or Senior Associate. Can refer other Associates, but this does not create a new commission tier.",
                  can: ["Refer other Associates (no override earned)", "Earn standard commissions on own deals", "Access authorized verticals"],
                },
              ].map(({ level, color, badge, desc, can }) => (
                <div key={level} className="border border-gray-100 rounded-xl p-4">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${badge}`}>{level}</span>
                  <p className="text-sm text-gray-600 mt-3 mb-3 leading-relaxed">{desc}</p>
                  <div className="space-y-1.5">
                    {can.map((item) => (
                      <div key={item} className="flex items-start gap-2 text-xs text-gray-700">
                        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color }} />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-bold text-gray-600 mb-3 uppercase tracking-wide">Hierarchy Structure</p>
              <div className="flex flex-col gap-1 font-mono text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" />
                  <span className="font-bold text-yellow-700">Agency</span>
                  <span className="text-gray-400 text-xs ml-1">← top level</span>
                </div>
                <div className="flex items-center gap-2 ml-5">
                  <span className="text-gray-300">└──</span>
                  <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                  <span className="font-bold text-blue-700">Senior Associate</span>
                </div>
                <div className="flex items-center gap-2 ml-12">
                  <span className="text-gray-300">└──</span>
                  <span className="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0" />
                  <span className="font-bold text-purple-700">Associate</span>
                  <span className="text-gray-400 text-xs ml-1">← max depth</span>
                </div>
              </div>
              <InfoBox type="warning">
                <strong>Maximum 3 levels.</strong> Associates can refer other Associates, but the referred Associate is independent — no additional commission tier is created.
              </InfoBox>
            </div>
          </section>

          {/* Referral Links */}
          <section id="referral" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-xl bg-[#84ab4a]/10 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-[#84ab4a]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Referral Links</h2>
                <p className="text-xs text-gray-500">How agents share their unique links with prospects</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed mb-5">
              Each agent has a unique referral link per vertical they are certified in. When a prospect clicks the link and fills out a form, the lead is automatically tagged to that agent's Rep Code in the system — no manual entry needed.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide rounded-tl-lg">Vertical</th>
                    <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide">Referral Link Format</th>
                    <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide rounded-tr-lg">Certification Required?</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { vertical: "Scale360", domain: "scale360.wibiz.ai", cert: "Yes" },
                    { vertical: "Fundable360", domain: "fundable360.wibiz.ai", cert: "Yes" },
                    { vertical: "Legacy360", domain: "legacy360.wibiz.ai", cert: "Yes" },
                    { vertical: "Share360 (Business)", domain: "share360.wibiz.ai", cert: "Yes" },
                    { vertical: "Share360 (Nonprofit)", domain: "share360.wibiz.ai", cert: "Yes" },
                    { vertical: "Media360", domain: "media360.wibiz.ai", cert: "Yes" },
                  ].map(({ vertical, domain, cert }, i) => (
                    <tr key={vertical} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{vertical}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-500">https://{domain}/?ref=BC360-XXXX</td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">{cert}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <InfoBox type="tip">
              Referral links are <strong>locked by default</strong> and only unlock after the agent passes the certification quiz for that vertical. This ensures every agent understands the product before promoting it.
            </InfoBox>
          </section>

          {/* Certifications */}
          <section id="certifications" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Certifications</h2>
                <p className="text-xs text-gray-500">How agents unlock their referral links</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
              {[
                { icon: FileText, color: "#1A6FD4", title: "Take the Quiz", desc: "7 multiple-choice questions per vertical. Covers product basics, eligibility, and the sales process." },
                { icon: CheckCircle2, color: "#22c55e", title: "Pass at 70%+", desc: "Agents need to answer at least 5 out of 7 questions correctly. They can retake the quiz if they don't pass." },
                { icon: Link2, color: "#84ab4a", title: "Links Unlock", desc: "Referral links for that vertical go live immediately after passing. The agent can start sharing right away." },
              ].map(({ icon: Icon, color, title, desc }) => (
                <div key={title} className="flex gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
                    <Icon className="w-4 h-4" style={{ color }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">{title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <InfoBox type="info">
              Certifications are per vertical — an agent certified in Fundable360 is not automatically certified in Scale360. Each vertical requires its own quiz. Admins can see each agent's certification status in the Reps tab.
            </InfoBox>
          </section>

          {/* Footer */}
          <div className="text-center py-6 text-xs text-gray-400">
            BizConnect360 — Agent Registration Guide &nbsp;·&nbsp; For internal use &nbsp;·&nbsp;{" "}
            <a href="mailto:info@BizConnect360.com" className="underline hover:text-gray-600">info@BizConnect360.com</a>
          </div>
        </main>
      </div>
    </div>
  );
}
